// Default lifecycle SMS templates per touchpoint.
//
// The send cron at /api/lifecycle/send.js reads from public.message_templates
// to know what body to send. If the operator has never seeded their
// templates, no rows exist there and every queued send gets skipped
// with 'no_template'. The Templates panel under Settings calls
// seedDefaultTemplatesIfEmpty(supabase, ownerId) on mount so this is
// usually a no-op for returning operators.
//
// Bodies use {merge_field} tokens — same as the cron's renderTemplate:
//   {first_name}        client's first name
//   {agent_name}        operator's display name (settings.ownerName)
//   {draft_date}        client.first_draft_date
//   {draft_amount}      formatted as $XX.XX
//   {referral_amount}   formatted as $XX
//   {carrier}           policy carrier
//
// Tone notes:
//   • Brief. SMS, not email. 1-2 sentences max.
//   • No emoji clutter — readable in any client.
//   • Reply STOP appended ONCE on the welcome message (carrier compliance).
//   • Marketing touches identify themselves as referral asks so the
//     recipient knows what's coming.

export const LIFECYCLE_TOUCHPOINTS = [
  {
    key: 'welcome',
    label: 'Welcome',
    description: 'Sent the day the policy closes.',
    anchor: 'close_date',
    offset: '+0 days',
    isMarketing: false,
    defaultBody:
      'Hi {first_name}, this is {agent_name}. Welcome aboard — your {carrier} policy is set up. Reply with any questions. Reply STOP to opt out.',
  },
  {
    key: 'first_draft',
    label: 'First Draft Reminder',
    description: 'One day before the first premium draft hits.',
    anchor: 'first_draft_date',
    offset: '-1 day',
    isMarketing: false,
    defaultBody:
      'Hi {first_name}, heads-up — your first premium draft of {draft_amount} hits tomorrow. Reply if anything looks off.',
  },
  {
    key: 'day30',
    label: '30-Day Check-In',
    description: 'Care touch 30 days after issue.',
    anchor: 'issue_date',
    offset: '+30 days',
    isMarketing: false,
    defaultBody:
      'Hi {first_name}, hope everything is smooth with your {carrier} policy. Anything you need from us? — {agent_name}',
  },
  {
    key: 'month3',
    label: '3-Month Referral Ask',
    description: 'Referral-eligible ask at 90 days.',
    anchor: 'issue_date',
    offset: '+90 days',
    isMarketing: true,
    defaultBody:
      'Hi {first_name}, hope you and the family are good. Quick favor — know anyone who needs coverage? We pay {referral_amount} for any referral that closes. — {agent_name}',
  },
  {
    key: 'month6',
    label: '6-Month Referral Ask',
    description: 'Referral-eligible ask at 180 days.',
    anchor: 'issue_date',
    offset: '+180 days',
    isMarketing: true,
    defaultBody:
      'Hi {first_name}, hope all is well. 6-month check-in — and if you know someone we should help, we pay {referral_amount} per referral. — {agent_name}',
  },
  {
    key: 'month9',
    label: '9-Month Referral Ask',
    description: 'Referral-eligible ask at 270 days.',
    anchor: 'issue_date',
    offset: '+270 days',
    isMarketing: true,
    defaultBody:
      'Hi {first_name}, almost a year on book. Anyone else in your circle could use a policy? {referral_amount} for any referral that closes. — {agent_name}',
  },
  {
    key: 'anniversary',
    label: 'Anniversary',
    description: 'Yearly anniversary on issue date. Stores as anniversary_{YYYY} in the queue.',
    anchor: 'issue_date',
    offset: '+365 days (recurs)',
    isMarketing: true,
    defaultBody:
      'Hi {first_name}, happy policy anniversary with {carrier}. If you know anyone we should help, our referral payout is {referral_amount}. — {agent_name}',
  },
  {
    key: 'appt_24h',
    label: 'Appointment Reminder (24h)',
    description: 'Auto-sent ~24 hours before an appointment in appointment_reminders.',
    anchor: 'appointment_at',
    offset: '-24 hours',
    isMarketing: false,
    defaultBody:
      'Hi {first_name}, reminder — we have an appointment tomorrow at {appt_time}. Reply if you need to reschedule. — {agent_name}',
  },
  {
    key: 'appt_1h',
    label: 'Appointment Reminder (1h)',
    description: 'Auto-sent ~1 hour before an appointment in appointment_reminders.',
    anchor: 'appointment_at',
    offset: '-1 hour',
    isMarketing: false,
    defaultBody:
      'Hi {first_name}, just a heads-up — our appointment is in about an hour. Talk soon. — {agent_name}',
  },
];

// Seed the operator's message_templates table with the defaults above
// if they have zero rows. Idempotent — safe to call on every mount.
export async function seedDefaultTemplatesIfEmpty(supabase, ownerId) {
  if (!supabase || !ownerId) return { seeded: 0 };
  const { count, error: countErr } = await supabase
    .from('message_templates')
    .select('id', { count: 'exact', head: true })
    .eq('owner_id', ownerId);
  if (countErr) return { error: countErr.message, seeded: 0 };
  if ((count ?? 0) > 0) return { seeded: 0 };

  const rows = LIFECYCLE_TOUCHPOINTS.map(t => ({
    owner_id:       ownerId,
    touchpoint_key: t.key,
    channel:        'sms',
    body:           t.defaultBody,
    is_marketing:   t.isMarketing,
    active:         true,
  }));
  const { error: insertErr } = await supabase
    .from('message_templates')
    .insert(rows);
  if (insertErr) return { error: insertErr.message, seeded: 0 };
  return { seeded: rows.length };
}
